import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMetadata } from 'contexts/Metadata';
import type { IGenericObject } from 'types';
import { useApp } from 'contexts/App';

export default function useOrderDetailsFields(transNamespace: string, isSummary: boolean = false) {
  const { t } = useTranslation(transNamespace);

  const {
    state: metadataState,
    selectors: { selectOfficeLocation },
  } = useMetadata({
    loadOfficeLocationsData: true,
  });

  const {
    state: { isGroupUser },
  } = useApp();
  const isSummaryForGroupUser = isGroupUser && isSummary;

  const displayOfficeLocation = useCallback(
    (locationCode) => {
      const officeLocation = selectOfficeLocation(metadataState, locationCode);

      return officeLocation ? officeLocation.locationName : '';
    },
    [metadataState, selectOfficeLocation],
  );

  return useMemo(
    () => [
      {
        title: t('OrderSummary.accordions.customerDetails'),
        fields: [
          {
            label: t('OrderSummary.fields.accountName'),
            accessor: 'customer.customerName',
            gridProps: {
              order: 1,
            },
          },
          {
            label: t('OrderSummary.fields.customer'),
            accessor: 'customer.customerReference',
            gridProps: {
              order: isSummaryForGroupUser ? 3 : 2,
            },
          },
          {
            label: t('OrderSummary.fields.multiAccount'),
            accessor: 'multiAccount',
            gridProps: {
              order: isSummaryForGroupUser ? 5 : 3,
            },
            hidden: ({ multiAccount }: IGenericObject) => !multiAccount,
          },
          {
            label: t('OrderSummary.fields.officeLocation'),
            accessor: 'officeLocation',
            displayValue: (value) => displayOfficeLocation(value),
            gridProps: {
              order: isSummaryForGroupUser ? 2 : 5,
            },
          },
          {
            label: t('OrderSummary.fields.customerNotes'),
            accessor: 'customerNotes',
            gridProps: {
              order: isSummaryForGroupUser ? 4 : 6,
            },
          },
        ],
        columns: isSummaryForGroupUser ? 2 : 1,
      },
      ...(!isGroupUser || !isSummary
        ? [
            {
              title: t('OrderSummary.accordions.orderDetails'),
              fields: [
                {
                  label: t('OrderSummary.fields.accountRepresentativeName'),
                  accessor: 'representative.representativeName',
                  hidden: isGroupUser,
                },
                {
                  label: t('OrderSummary.fields.accountRepresentativeCode'),
                  accessor: 'representative.representativeCode',
                  hidden: isGroupUser,
                },
                {
                  label: t('OrderSummary.fields.accountRepresentativeEmail'),
                  accessor: 'representative.representativeEmail',
                  hidden: isGroupUser,
                },
                {
                  label: t('OrderSummary.fields.createdBy'),
                  accessor: 'user.userName',
                  hidden: isSummary,
                },
                {
                  label: t('OrderSummary.fields.createdByEmail'),
                  accessor: 'user.userEmail',
                  hidden: isSummary,
                },
              ],
            },
          ]
        : []),
    ],
    [t, isSummaryForGroupUser, isGroupUser, isSummary, displayOfficeLocation],
  );
}
